// LE MODE SELECTION du champ a valeurs de la rangee 3 d'une fiche
// (`docs/maquettes/atelier-2610/README.md`, section 3), pour des valeurs qu'on
// veut choisir vite mais qui ne forment PAS un parcours (une categorie, un
// type, un canal). Rien n'y dit « avant » ni « apres » : pas de chevrons, pas
// de coche, pas de lime plein. L'autre mode est le MODE FLUX, les etapes en
// chevrons (`BarreDEtapes.tsx`) ; la porte choisit, champ par champ.
//
// LE DESSIN est celui des ETIQUETTES des badges de l'app Flux
// (`apps/hello/src/apps/flux/builder/flux-ap.css`), retenu par le fondateur le
// 27/10 parmi quatre essais.
import type { JSX } from 'react';
import { IconPadlockSquare1 } from '../icons/index.js';
import type { Couleur } from '@taki/data-model';
import { cn } from '../lib/cn.js';
import { couleurCss } from '../lib/couleurs.js';
import type { ValeurDEtape } from './fiche-contrat.js';
import { useGroupeDeChoix, ZoneQuiDefile } from './ZoneQuiDefile.js';

/**
 * LE VERROU D'UNE PISTE, tel que les deux modes le recoivent : quelles valeurs
 * (par index) ne se choisissent plus, et pourquoi (chantier `etapes-verrou`).
 */
export interface VerrouDeLaPiste {
  verrouillee: (index: number) => boolean;
  /** Pourquoi CETTE valeur est fermee : la raison du premier verrou qui la ferme. */
  raison: (index: number) => string;
}

/**
 * LES ETIQUETTES. Fin contour gris, texte de 12 px, chacune avec le POINT de la
 * couleur de son option (le rendu de couleur des catalogues, `couleurCss`) ;
 * la choisie prend le contour, le fond et l'encre de l'accent, son point est
 * plein, les autres un peu estompes, et leur contour fonce au survol. Chaque
 * etiquette a la largeur de son nom. Defilante comme la piste des
 * etapes (`ZoneQuiDefile`), la valeur choisie toujours visible.
 */
export function SelectionDeFiche({
  libelle,
  valeurs,
  courante,
  choisir,
  verrou,
}: {
  /** Le nom du champ, pour les lecteurs d'ecran. */
  libelle: string;
  valeurs: readonly ValeurDEtape[];
  /** L'index de la valeur courante, `-1` sans valeur. */
  courante: number;
  /** Nul : la selection est inerte (sa raison est portee par la bande). */
  choisir: ((index: number) => void) | null;
  /** Des valeurs verrouillees par une condition de la fiche : cadenas et raison sur chacune. */
  verrou?: VerrouDeLaPiste;
}): JSX.Element {
  const groupe = useGroupeDeChoix(courante, valeurs.length, choisir, verrou?.verrouillee);
  return (
    <ZoneQuiDefile cle={`${courante}-${valeurs.length}`}>
      <div role="radiogroup" aria-label={libelle} onKeyDown={groupe.onKeyDown} className="flex h-8 w-fit items-center gap-1.5">
        {valeurs.map((valeur, index) => {
          const active = index === courante;
          const verrouillee = choisir != null && !active && verrou?.verrouillee(index) === true;
          return (
            <button
              key={valeur.id}
              {...groupe.radio(index)}
              aria-label={valeur.libelle}
              {...(verrouillee ? { title: `${valeur.libelle} : ${verrou!.raison(index)}`, 'aria-description': verrou!.raison(index) } : {})}
              style={{ transitionDuration: 'var(--dur-base)', transitionTimingFunction: 'var(--ease-standard)' }}
              className={cn(
                'relative flex h-7 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-sm border px-2.5 text-xs font-medium',
                'transition-[background-color,color,border-color] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border',
                choisir == null ? 'cursor-default' : verrouillee ? 'cursor-not-allowed' : 'cursor-pointer',
                active
                  ? 'border-accent-border bg-accent-tint text-accent-ink'
                  : cn(
                      'border-border bg-surface text-ink-700',
                      choisir != null && !verrouillee && 'hover:border-border-strong hover:text-ink-900',
                      verrouillee && 'opacity-60',
                    ),
              )}
            >
              {/* LE CADENAS prend la place du point, dans la meme case de 0.5rem : l'etiquette ne change pas de largeur. */}
              <span
                aria-hidden
                style={verrouillee ? undefined : { backgroundColor: couleurCss(valeur.couleur as Couleur | undefined) }}
                className={cn('relative h-2 w-2 shrink-0 rounded-full transition-opacity', active ? 'opacity-100' : 'opacity-60')}
              >
                {verrouillee ? (
                  <IconPadlockSquare1 size={10} data-testid="cadenas-de-valeur" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
                ) : null}
              </span>
              {valeur.libelle}
            </button>
          );
        })}
      </div>
    </ZoneQuiDefile>
  );
}
